<template>
    <div id="container" type="vaboutus">
        <div class="page page-aboutus" v-ga:request="{pageType:'vaboutus', state: {}}" v-ga:page="{pageType:'vaboutus', glb: glb, state: {}}">
            <mvtHeader />

            <section class="about-hero inline bg-white">
                <div class="fixed fixed-max-sm">
                    <div class="hero-brand text-center text-bold">Movoto by Lower</div>
                    <h1 class="hero-title text-center text-bold">A smarter path to <br> homeownership</h1>
                </div>
            </section>

            <section class="about-intro inline bg-white">
                <div class="fixed fixed-max-sm intro-grid">
                    <div class="intro-copy">
                        <p>
                            Movoto is a Top 5 U.S. real estate portal that helps people buy and sell homes. We combine home search, local market insights, and trusted professional
                            connections to support millions of consumers nationwide.
                        </p>
                        <p>
                            By partnering with highly experienced, vetted local real estate agents, we connect consumers with the right professionals based on their goals, location, and
                            needs - simplifying complex decisions and supporting long-term homeownership.
                        </p>
                        <p>
                            As part of <a class="link" href="https://lower.com/" target="_blank" rel="noopener">Lower</a>, we connect buyers with local loan officers when financing is needed, aligning
                            search, local expertise, and financing from day one.
                        </p>
                    </div>
                    <div class="intro-image">
                        <img :src="heroImage" alt="Movoto team helping homeowners" loading="lazy" />
                    </div>
                </div>
            </section>

            <section class="about-how inline">
                <div class="fixed fixed-max-sm">
                    <h2 class="section-title">How Movoto works</h2>
                    <p class="section-subtitle">
                        We meet people where their homeownership journey begins—online. Our portal helps them explore homes, neighborhoods, and local market conditions, whether they are
                        actively buying or just starting to understand their options.
                    </p>
                    <a class="btn secondary medium" href="/" >Get Started</a>

                    <div class="how-grid">
                        <article class="how-card" v-for="item in howMovotoWorks" :key="item.title">
                            <div class="how-icon">
                                <span v-if="item.iconSvg" class="how-icon-svg" v-html="item.iconSvg"></span>
                                <i v-else :class="item.icon"></i>
                            </div>
                            <h3 class="text-bold">{{ item.title }}</h3>
                            <p>{{ item.text }}</p>
                        </article>
                    </div>
                </div>
            </section>

            <section class="about-impact inline bg-white">
                <div class="fixed fixed-max-sm">
                    <h2 class="section-title">Our impact</h2>
                    <div class="impact-grid">
                        <div>
                            <div class="impact-stat">50,000,000+<span>Consumers per year</span></div>
                            <img class="impact-image" :src="impactImages.left" alt="Real estate guidance" loading="lazy" />
                        </div>
                        <div>
                            <img class="impact-image" :src="impactImages.center" alt="Consumers and agents" loading="lazy" />
                            <div class="impact-stat">12,000+<span>Real estate agents</span></div>
                        </div>
                        <div>
                            <div class="impact-stat">500+<span>Loan officers</span></div>
                            <img class="impact-image" :src="impactImages.right" alt="Team collaboration" loading="lazy" />
                        </div>
                    </div>
                </div>
            </section>

            <section class="about-recognition inline">
                <div class="fixed fixed-max-sm">
                    <h2 class="section-title">Recognition and industry leadership</h2>
                    <p class="section-subtitle">
                        We're proud of what we're building and the team behind it. As we grow, we remain committed to building a platform that serves homeowners at every stage.
                        <a class="link" href="https://www.lower.com/news" target="_blank" rel="noopener">See our latest news and awards.</a>
                    </p>
                    <div class="recognition-grid">
                        <article class="recognition-card" v-for="card in recognitionCards" :key="`${card.title}-${card.year}`">
                            <img :src="card.image" :alt="card.title" loading="lazy" />
                            <div class="recognition-copy">
                                <h3 class="text-bold">{{ card.title }}</h3>
                                <div class="year">{{ card.year }}</div>
                                <p>{{ card.text }}</p>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <section class="about-careers inline bg-white">
                <div class="fixed fixed-max-sm careers-grid">
                    <div>
                        <h2 class="section-title">Careers</h2>
                        <p class="section-subtitle">
                            At Movoto, we're building a smarter, more connected path to homeownership. As part of Lower, we combine real estate search, local expertise, and financing to support millions of buyers and sellers nationwide. If you're excited about shaping the future of homeownership and making a real impact at scale, we'd love to meet you.
                        </p>
                        <a class="btn secondary medium" href="https://careers.lower.com/" target="_blank" rel="noopener">Explore Open Positions</a>
                    </div>
                    <div class="careers-image">
                        <img :src="assets[7]" alt="Movoto careers" loading="lazy" />
                    </div>
                </div>
            </section>

            <section class="about-ratings inline">
                <div class="fixed fixed-max-sm">
                    <h2 class="section-title">Movoto ratings and reviews</h2>
                    <p class="section-subtitle">
                        Here's what customers are saying about their experiences with Movoto. With a high Trustpilot rating and an accredited business status from the BBB, we take pride in our commitment to quality service. If you have a story to share, feel free to <a class="link" href="https://www.trustpilot.com/review/movoto.com" target="_blank" rel="noopener">leave a review here</a>.
                    </p>

                    <div class="ratings-grid">
                        <div class="ratings-stats">
                            <div class="rating-item">
                                <div class="score">A+</div>
                                <div><img :src="assets[8]" alt="BBB rating" loading="lazy" /></div>
                            </div>
                            <div class="rating-item">
                                <div class="score">4.3</div>
                                <div><img :src="assets[9]" alt="Trustpilot rating" loading="lazy" /></div>
                            </div>
                        </div>
                        <div class="testimonial-grid">
                            <article class="testimonial-card" v-for="(item, idx) in testimonials" :key="idx">
                                <p class="quote">{{ item.text }}</p>
                                <img class="stars" :src="assets[10]" alt="5 star rating" loading="lazy" />
                                <div class="name">{{ item.name }}</div>
                                <div class="role">{{ item.role }}</div>
                            </article>
                        </div>
                    </div>
                </div>
            </section>

            <section class="about-support inline bg-white">
                <div class="fixed fixed-max-sm">
                    <h2 class="section-title">Have questions or need support?</h2>
                    <div class="support-grid">
                        <div class="support-copy">
                            <div class="support-item">
                                <i class="f1 icon-phone-o"></i>
                                <h3 class="f4 text-bold"><a class="link" href="tel:18444170593">(844) 417-0593</a></h3>
                                <p>Contact our concierge for assistance with your home search needs.</p>
                            </div>
                            <div class="support-item">
                                <i class="f1 icon-envelope-o"></i>
                                <h3 class="f4 text-bold">Customer support</h3>
                                <p>Send an <a class="link" href="mailto:contact@movoto.com">email</a> with your questions, comments, suggestions or concerns.</p>
                            </div>
                            <div class="support-item">
                                <i class="f1 icon-envelope-o"></i>
                                <h3 class="f4 text-bold">Press Inquiries</h3>
                                <p>For all media inquiries, please contact <a class="link" href="mailto:press@movoto.com">press@movoto.com</a>.</p>
                            </div>
                        </div>
                        <mvtHotleadForm v-if="hotleadOpt" :options="hotleadOpt" />
                    </div>
                </div>
            </section>

            <mvtFooter />
        </div>
        <mvtPopSignin />
        <mvtDialogCenter></mvtDialogCenter>
    </div>
</template>

<script src="./aboutus.js"></script>
<style lang="scss">
@use "./aboutus.scss" as *;
</style>
